import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { computeTaxes, solveGrossFromNet } from "../controllers/taxes";
import { trackEvent } from "../utils/analytics";

const DEFAULT_CFG = {
  ref_values: [
    { year: 2025, mzp: 85000, mrp: 3932 },
    { year: 2026, mzp: 85000, mrp: 4325 },
  ],
  deductions: [
    { key: "opv_pct", label: "Вычет ОПВ", type: "percent_gross", value: 10, unit: "tenge", enabled: true },
    { key: "vosms_pct", label: "Вычет ВОСМС", type: "percent_gross", value: 2, unit: "tenge", enabled: true },
    { key: "std_mrp", label: "Стандартный вычет (30 МРП)", type: "absolute", value: 30, unit: "mrp", enabled: true },
  ],
  taxes: [
    { key: "opv", label: "ОПВ", rate: 10, base: "gross", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 50, deduction_keys: [] },
    { key: "vosms", label: "ВОСМС", rate: 2, base: "gross", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 20, deduction_keys: [] },
    { key: "ipn", label: "ИПН", rate: 10, base: "after_deductions", apply_to: "employee", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: ["opv_pct", "vosms_pct", "std_mrp"] },
    { key: "so", label: "СО", rate: 5, base: "after_deductions", apply_to: "employer", enabled: true, min_base_mzp: 1, max_base_mzp: 7, deduction_keys: ["opv_pct"] },
    { key: "oosms", label: "ООСМС", rate: 3, base: "gross", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 40, deduction_keys: [] },
    { key: "opvr", label: "ОПВР", rate: 3.5, base: "gross", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 50, deduction_keys: [] },
    { key: "sn", label: "Соцналог", rate: 6, base: "after_deductions", apply_to: "employer", enabled: true, min_base_mzp: 0, max_base_mzp: 0, deduction_keys: ["opv_pct", "vosms_pct"] },
  ],
};

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("ru-RU");

export default function HomePayCalculator({ className = "" }) {
  const { t } = useTranslation();
  const initialParams = (() => {
    try {
      const u = new URL(window.location.href);
      const n = Number(u.searchParams.get("calc"));
      const m = u.searchParams.get("mode");
      return {
        amount: n > 0 && n < 100_000_000 ? n : 300000,
        mode: m === "net" ? "net" : "gross",
      };
    } catch { return { amount: 300000, mode: "gross" }; }
  })();
  const [amount, setAmount] = useState(initialParams.amount);
  const [mode, setMode] = useState(initialParams.mode);
  const [copied, setCopied] = useState(false);
  const year = new Date().getFullYear();
  const gross = useMemo(() => {
    const a = Number(amount) || 0;
    return mode === "net" ? solveGrossFromNet(a, DEFAULT_CFG, { year }) : a;
  }, [amount, mode, year]);
  const result = useMemo(
    () => computeTaxes(gross, DEFAULT_CFG, null, { year }),
    [gross, year],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (Number(amount) > 0) {
        trackEvent("calc_use", { label: mode });
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [amount, mode]);

  const onShare = async () => {
    const url = `${window.location.origin}/?calc=${Number(amount) || 0}&mode=${mode}#calc`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: t("landing.calc.share_title"),
          text: t("landing.calc.share_text", { gross: fmt(gross), net: fmt(result.net) }),
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // user cancelled
    }
  };

  return (
    <div className={`card shadow-sm border-0 h-100 landing-demo-calc-card ${className}`.trim()} data-testid="landing-calc-card">
      <div className="card-body p-4 d-flex flex-column">
        <div className="d-flex align-items-center mb-3">
          <i className="bi bi-calculator-fill text-primary fs-3 me-2"></i>
          <div>
            <h3 className="h5 mb-0" id="calc-title">{t("landing.calc.title")}</h3>
            <div className="small text-muted">{t("landing.calc.subtitle")}</div>
          </div>
        </div>

        <div className="btn-group btn-group-sm w-100 mb-3" role="group" aria-label="mode">
          <button
            type="button"
            className={`btn ${mode === "gross" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setMode("gross")}
            data-testid="landing-calc-mode-gross"
          >
            {t("landing.calc.mode_gross")}
          </button>
          <button
            type="button"
            className={`btn ${mode === "net" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setMode("net")}
            data-testid="landing-calc-mode-net"
          >
            {t("landing.calc.mode_net")}
          </button>
        </div>

        <label className="form-label small text-muted mb-1">
          {mode === "net" ? t("landing.calc.net_input_label") : t("landing.calc.gross_label")}
        </label>
        <div className="input-group mb-3">
          <input
            type="number"
            className="form-control"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
            step="10000"
            data-testid="landing-calc-input"
            aria-labelledby="calc-title"
          />
          <span className="input-group-text">{t("landing.calc.per_month")}</span>
        </div>

        {mode === "net" && (
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <span className="text-muted small">{t("landing.calc.gross_result_label")}</span>
            <strong className="text-primary text-nowrap" style={{ fontSize: 18 }} data-testid="landing-calc-gross-result">
              {fmt(gross)} ₸
            </strong>
          </div>
        )}

        <div className="small flex-grow-1">
          {["opv", "vosms", "ipn"].map((k) => result.byTax[k] && (
            <div key={k} className="d-flex justify-content-between align-items-baseline gap-2 py-1">
              <span className="text-muted">{t(`landing.calc.${k}`)}</span>
              <strong className="text-nowrap" data-testid={`landing-calc-${k}`}>−{fmt(result.byTax[k].amount)} ₸</strong>
            </div>
          ))}
          <div className="text-muted mt-2 mb-1">{t("landing.calc.employer")}</div>
          {["so", "oosms", "opvr", "sn"].map((k) => result.byTax[k] && (
            <div key={k} className="d-flex justify-content-between align-items-baseline gap-2 py-1">
              <span className="text-muted">{t(`landing.calc.${k}`)}</span>
              <span className="text-nowrap fw-semibold text-secondary" data-testid={`landing-calc-${k}`}>
                {fmt(result.byTax[k].amount)} ₸
              </span>
            </div>
          ))}
        </div>

        <hr />
        <div className="d-flex align-items-baseline">
          <span className="text-muted">{t("landing.calc.net_label")}</span>
          <strong className="ms-auto text-success" style={{ fontSize: 24 }} data-testid="landing-calc-net">
            {fmt(result.net)} ₸
          </strong>
        </div>

        <div className="d-flex align-items-center mt-3 gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={onShare}
            data-testid="landing-calc-share"
          >
            <i className={`bi ${copied ? "bi-check2" : "bi-share"} me-1`}></i>
            {copied ? t("landing.calc.copied") : t("landing.calc.share")}
          </button>
          <div className="small text-muted">
            <i className="bi bi-info-circle me-1"></i>
            {t("landing.calc.footnote")}
          </div>
        </div>
      </div>
    </div>
  );
}
